// src/components/DatePicker.tsx
//
// THE house date field: a text input the user can type into, with a calendar
// that POPS UP beneath it. Every date an officer chooses in this product goes
// through this one control — the medical service date (`ServiceDatePicker`, a
// one-line wrapper over this), the administrative effective dates on every
// supersede form, the report date range, the retention window.
//
// ★ 2026-08-26 (Wave 26) — GENERALISED from `ServiceDatePicker`, by the owner's
// direction, after a walk of the running product found two calendars: this one
// on the entitlement enquiry, and the browser's own `<input type="date">` —
// `mm/dd/yyyy`, Sunday-first, unstyleable — on sixteen other fields across
// fourteen screens. Those fields had stayed native for a stated reason (the
// picker's labels said "service date" throughout, and an administrative
// effective-date is not a clinical one), which is exactly what the `subject`
// prop below removes: the labels now say what the field is for. One calendar,
// everywhere, in the product's own type, colour and week order.
//
// ## Why the field is ours and not `<input type="date">`
//
// The native control cannot be restyled — a browser paints its own picker and
// exposes nothing to CSS. Keeping the native field while asking for a particular
// calendar is not an option the platform offers, so the field and the popover are
// both ours. What is preserved from the native version is the thing that mattered:
// the value is the ISO `YYYY-MM-DD` string the API already expects, with no
// localised format to parse on the way in or out.
//
// ## Why not `<sgds-datepicker>`
//
// Its value is `dd/mm/yyyy`, so consuming it means parsing a localised format on
// fields whose entire job is "never guess the date".
//
// **The cost, recorded rather than absorbed:** P2C1 §12.86(d) mandates SGDS
// alignment and P2C9 makes anything we build ours to keep current for the life of
// the contract at no additional cost. This is a component we own. It is aligned to
// SGDS *tokens* throughout — spacing, type, neutrals, focus ring, semantic colour
// roles — but it is not an SGDS *component*, and that distinction belongs in any
// compliance answer we give. Recorded in docs/design/sgds-reconciliation.md.
//
// ## Accessibility
//
// The input owns the value and can be typed into directly. The popover is a
// `role="dialog"` containing a `role="grid"` with roving tabindex: arrows move by
// day and week, PageUp/PageDown by month, Home/End across the week, Enter or Space
// chooses, Escape closes and returns focus to the field. Selection is a filled disc
// with white text — never a shadow, which would vanish for anyone who cannot
// perceive the lobes.
//
// ⚠ Labelling: pass `labelledBy` when the label is a separate element, or wrap
// the control in a `<label>` — the label then names the FIRST labelable
// descendant, which is the text input (the trigger button comes after it), and a
// click on the trigger is not redirected to the input because the target is
// itself interactive content. Both forms are in use; both are tested.
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

/** Monday-first, matching the reference and the local convention. */
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

/** All date maths is UTC. A local-timezone `Date` shifts the calendar day either
 *  side of midnight, which on this product changes which entitlement applies. */
function utc(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day))
}

/**
 * `en-CA` formats as `YYYY-MM-DD` directly — no localised string to parse on
 * the way back out, the same property this file's ISO handling relies on
 * everywhere else. Built once at module scope, like `format.ts`'s `DATE`/
 * `INSTANT` formatters.
 */
const SGT_TODAY_FORMAT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' })

/**
 * I8 — "today", as the Singapore civil day, not the UTC day.
 *
 * ⚠ This is NOT the same question as the UTC calendar maths everywhere else in
 * this file. Those uses (`utc()`, `toIso()`, `fromIso()`) are about VALUE
 * STABILITY: once a date is chosen, it must render as the same calendar day to
 * every viewer regardless of browser timezone, so this file fixes the
 * arithmetic to one timezone (UTC) rather than the viewer's local one. "What is
 * today" is a different question — it is not a stored value being displayed
 * consistently, it is "which day is it right now for the officer looking at
 * this screen", and that is inherently a question about the SINGAPORE civil
 * day, because that is where this service operates. `getUTCFullYear()` /
 * `getUTCMonth()` / `getUTCDate()` on `new Date()` answers "what day is it in
 * UTC right now", which is a DIFFERENT day from Singapore's for every instant
 * between 00:00 and 08:00 SGT (UTC+8) — exactly the early-shift window an
 * officer is most likely to reach for "today". Using `Intl.DateTimeFormat`
 * with an explicit Singapore zone answers the question this component is
 * actually asking, independent of the machine's own local timezone too.
 */
export function singaporeToday(): string {
  return SGT_TODAY_FORMAT.format(new Date())
}
function toIso(date: Date): string {
  return date.toISOString().slice(0, 10)
}
function fromIso(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return Number.isNaN(parsed.getTime()) || toIso(parsed) !== value ? null : parsed
}
/** 0 = Monday … 6 = Sunday. `getUTCDay()` is Sunday-first, so it is shifted. */
function mondayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7
}

interface DayCell {
  readonly date: Date
  readonly iso: string
  readonly inMonth: boolean
}

/** Six weeks, always — a grid that changes height as you page months makes the
 *  controls jump under the pointer. */
function buildWeeks(year: number, month: number): DayCell[][] {
  const first = utc(year, month, 1)
  const start = utc(year, month, 1 - mondayIndex(first))
  const cells: DayCell[] = Array.from({ length: 42 }, (_, index) => {
    const date = utc(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + index)
    return { date, iso: toIso(date), inMonth: date.getUTCMonth() === month }
  })
  return Array.from({ length: 6 }, (_, week) => cells.slice(week * 7, week * 7 + 7))
}

export interface DatePickerProps {
  /** ISO `YYYY-MM-DD`, or `''` for "not chosen". **Never defaulted by this component.** */
  readonly value: string
  readonly onChange: (iso: string) => void
  readonly labelledBy?: string
  readonly describedBy?: string
  /** Sets `aria-invalid` on the text field; pair it with `describedBy` naming the error text. */
  readonly invalid?: boolean
  /**
   * Mirrors the native `required` the fields this replaced carried: the form
   * refuses to submit while the field is empty, and assistive technology hears
   * `aria-required`. The visible asterisk stays the caller's, beside its label.
   */
  readonly required?: boolean
  /**
   * What the date IS, in the words the popover uses: "Choose a {subject}" and
   * "No {subject} chosen". Defaults to the plain `date`; `ServiceDatePicker`
   * passes `service date`. Never a sentence — it is spliced into two.
   */
  readonly subject?: string
  /**
   * Told about every keystroke in the text field: the raw text, and whether it is a complete real date (or empty).
   * The field itself never holds a half-typed or impossible date (`onChange` gets `''`), so a form that wants to say
   * "2026-02-30 is not a real date" needs this to hear what was typed.
   */
  readonly onTyped?: (raw: string, valid: boolean) => void
}

export type PopoverPlacement = 'top' | 'bottom'

/** The gap between the field and its calendar, in px; the same 8px the stylesheet leaves. */
const POPOVER_GAP = 8

/**
 * Open BELOW the field when the calendar fits there, otherwise on whichever side has more room. A field at the very
 * top of a short window has more room below even if neither fits, so it stays below; a field near the bottom opens
 * upward, which is the case that used to push the month grid below the fold and make the officer scroll.
 */
export function choosePopoverPlacement(
  field: { readonly top: number; readonly bottom: number },
  popoverHeight: number,
  viewportHeight: number,
): PopoverPlacement {
  const below = viewportHeight - field.bottom
  const above = field.top
  if (below >= popoverHeight + POPOVER_GAP) return 'bottom'
  return above > below ? 'top' : 'bottom'
}

export function DatePicker({ value, onChange, labelledBy, describedBy, invalid = false, required = false, subject = 'date', onTyped }: DatePickerProps) {
  const popoverId = useId()
  const [open, setOpen] = useState(false)
  // Which side of the field the calendar opens on. Decided by what FITS (see choosePopoverPlacement), so a field near
  // the bottom of the screen opens its calendar upward instead of below the fold (UI feedback, 2026-10-14).
  const [placement, setPlacement] = useState<PopoverPlacement>('bottom')
  const popRef = useRef<HTMLDivElement>(null)
  const [typed, setTyped] = useState(value)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const selected = useMemo(() => fromIso(value), [value])

  // The month on screen. Opens on the chosen date when there is one, and on the
  // current month only as a place to LOOK — never as a value. Nothing here writes
  // a date the user did not choose. "Current month" is Singapore's, via
  // singaporeToday() — the same I8 fix as todayIso below, for the same reason:
  // this is "what month is it for the officer right now", not a stored value.
  const [cursor, setCursor] = useState<Date>(() => {
    if (selected !== null) return selected
    const today = fromIso(singaporeToday())
    return today === null ? utc(1970, 0, 1) : utc(today.getUTCFullYear(), today.getUTCMonth(), 1)
  })
  const [focusedIso, setFocusedIso] = useState<string>(() => toIso(selected ?? cursor))

  useEffect(() => { setTyped(value) }, [value])
  // Measured before paint, so the calendar never flashes below and then jumps. Re-measured on resize and scroll
  // because the room above and below changes with either.
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const root = rootRef.current
      const pop = popRef.current
      if (root === null || pop === null) return
      const rect = root.getBoundingClientRect()
      setPlacement(choosePopoverPlacement({ top: rect.top, bottom: rect.bottom }, pop.offsetHeight, window.innerHeight))
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])
  useEffect(() => {
    if (selected === null) return
    setCursor(utc(selected.getUTCFullYear(), selected.getUTCMonth(), 1))
    setFocusedIso(toIso(selected))
  }, [selected])

  // Close on outside click and on Escape. Escape returns focus to the field —
  // a popover that closes and drops focus to the body strands a keyboard user.
  useEffect(() => {
    if (!open) return
    const onDocDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); inputRef.current?.focus() }
    }
    document.addEventListener('mousedown', onDocDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    gridRef.current?.querySelector<HTMLElement>('[data-focused="true"]')?.focus({ preventScroll: true })
  }, [open, focusedIso])

  const year = cursor.getUTCFullYear()
  const month = cursor.getUTCMonth()
  const weeks = useMemo(() => buildWeeks(year, month), [year, month])
  // I8: the Singapore civil day, not `new Date().getUTCFullYear()/getUTCMonth()/
  // getUTCDate()` — see singaporeToday()'s comment for why this is a different
  // question from the UTC value-stability maths elsewhere in this file.
  const todayIso = singaporeToday()

  const shiftMonth = useCallback((delta: number) => {
    setCursor((current) => utc(current.getUTCFullYear(), current.getUTCMonth() + delta, 1))
  }, [])

  const moveFocus = useCallback((days: number) => {
    setFocusedIso((current) => {
      const base = fromIso(current) ?? cursor
      const next = utc(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + days)
      if (next.getUTCMonth() !== month || next.getUTCFullYear() !== year) {
        setCursor(utc(next.getUTCFullYear(), next.getUTCMonth(), 1))
      }
      return toIso(next)
    })
  }, [cursor, month, year])

  const choose = (iso: string) => {
    onChange(iso)
    setOpen(false)
    inputRef.current?.focus()
  }

  const onGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const focused = fromIso(focusedIso)
    const handlers: Record<string, () => void> = {
      ArrowLeft: () => moveFocus(-1),
      ArrowRight: () => moveFocus(1),
      ArrowUp: () => moveFocus(-7),
      ArrowDown: () => moveFocus(7),
      Home: () => moveFocus(-(focused === null ? 0 : mondayIndex(focused))),
      End: () => moveFocus(6 - (focused === null ? 0 : mondayIndex(focused))),
      PageUp: () => shiftMonth(-1),
      PageDown: () => shiftMonth(1),
      Enter: () => choose(focusedIso),
      ' ': () => choose(focusedIso),
    }
    const handler = handlers[event.key]
    if (handler === undefined) return
    event.preventDefault()
    handler()
  }

  const commitTyped = (raw: string) => {
    setTyped(raw)
    onTyped?.(raw, raw === '' || fromIso(raw) !== null)
    // Only a complete, real ISO date commits. A partial string leaves the value
    // empty rather than half-parsed — this field must never hold a guess.
    onChange(fromIso(raw) === null ? '' : raw)
  }

  return (
    <div className="pt-dp" ref={rootRef}>
      <div className="pt-dp-field">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="pt-dp-input"
          placeholder="yyyy-mm-dd"
          value={typed}
          required={required}
          aria-required={required || undefined}
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={(event) => commitTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true) }
          }}
        />
        <button
          type="button"
          className="pt-dp-trigger"
          aria-label={open ? `Close the calendar for ${subject}` : `Open the calendar for ${subject}`}
          aria-expanded={open}
          aria-controls={popoverId}
          onClick={() => setOpen((previous) => !previous)}
        >
          <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
            <rect x="2.5" y="4" width="15" height="13.5" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {open && (
        <div ref={popRef} className="pt-dp-pop" data-placement={placement} id={popoverId} role="dialog" aria-modal="false" aria-label={`Choose a ${subject}`}>
          <div className="pt-dp-head">
            <button type="button" className="pt-dp-step" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="pt-dp-title" aria-live="polite">{MONTHS[month]} {year}</div>
            <button type="button" className="pt-dp-step" onClick={() => shiftMonth(1)} aria-label="Next month">
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>

          <div className="pt-dp-weekdays" aria-hidden="true">
            {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
          </div>

          <div ref={gridRef} role="grid" aria-label={`${MONTHS[month]} ${year}`} onKeyDown={onGridKeyDown}>
            {weeks.map((week) => (
              <div key={week[0]?.iso ?? ''} role="row" className="pt-dp-week">
                {week.map((cell) => {
                  const isSelected = value === cell.iso
                  const isFocused = focusedIso === cell.iso
                  return (
                    <button
                      key={cell.iso}
                      type="button"
                      role="gridcell"
                      data-focused={isFocused}
                      tabIndex={isFocused ? 0 : -1}
                      aria-selected={isSelected}
                      aria-current={cell.iso === todayIso ? 'date' : undefined}
                      className={[
                        'pt-dp-day',
                        cell.inMonth ? '' : 'pt-dp-day--outside',
                        isSelected ? 'pt-dp-day--selected' : '',
                        cell.iso === todayIso ? 'pt-dp-day--today' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => { setFocusedIso(cell.iso); choose(cell.iso) }}
                    >
                      {cell.date.getUTCDate()}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>

          <div className="pt-dp-foot">
            <button type="button" className="pt-dp-link" onClick={() => { onChange(''); setTyped(''); }} disabled={value === ''}>
              Clear
            </button>
            <span className="pt-dp-chosen">
              {value === '' ? `No ${subject} chosen` : value}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
